import {
  Empty,
  Button,
  useBreadcrumbRoutes,
  useUpdate,
  Wrapper,
  FormTitle,
  Input,
  Dropdown,
  Tree,
  Modal,
} from '@maxtropy/components';
import styles from './index.module.scss';
import React, { FC, Key, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Space } from 'antd';
import { LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import cabinetIcon from './imgs/cabinet.svg';
import circuitIcon from './imgs/circuit.png';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';
import CircuitList from './components/CircuitList';
import AddCabinetModal from './components/AddCabinetModal';
import AddRoomModal from './components/AddRoomModal';
import {
  apiV2DistributionCabinetDelDistributionRoomPost,
  apiV2DistributionCabinetDelPost,
  apiV2DistributionCabinetRoomCabinetTreePost,
  apiV2DistributionCabinetRoomCabinetListPost,
} from '@maxtropy/device-customer-apis-v2';
import { DataNode } from 'antd/es/tree';
import TreeNode from './components/TreeNode';
import { FunCallback, NodeType, RoomItem } from './utils';
import DragResize from '@/components/DragResize';
import { isNil } from 'lodash-es';
import AddVideoModal from './components/AddVideoModal';

const { Search } = Input;
const { Sider, Content } = Layout;

// 检查节点是否包含搜索关键字
const nodeContainFilterTxt = (node: any, filterTxt?: string): boolean => {
  if (!filterTxt) return true;
  return node.name?.includes(filterTxt || '');
};

// 获取匹配节点的所有父级路径
const getFilteredTreeData = (tree: any[], filterText?: string): any[] => {
  if (!filterText) return tree;

  const traverse = (nodes: any[]): any[] => {
    const result: any[] = [];

    for (const node of nodes) {
      // 如果当前节点匹配，则保留整个节点
      if (nodeContainFilterTxt(node, filterText)) {
        result.push({ ...node });
        continue;
      }

      // 如果当前节点不匹配，但有子节点，则递归检查子节点
      if (node.children && node.children.length > 0) {
        const filteredChildren = traverse(node.children);
        // 如果子节点中有匹配的，则保留当前节点和匹配的子节点
        if (filteredChildren.length > 0) {
          result.push({
            ...node,
            children: filteredChildren,
          });
        }
      }
    }

    return result;
  };

  return traverse(tree);
};

const loop = (
  treeData: any[],
  showIcon: boolean,
  searchValue: string,
  edit: FunCallback,
  editVideo: FunCallback,
  remove: FunCallback,
  hasEditPermission: boolean,
  hasRemovePermission: boolean
): DataNode[] => {
  return (treeData ?? []).map(item => {
    let children = item.children ?? [];
    children = [...children].sort((a, b) => {
      // 配电柜(CABINET)在前，配电房(ROOM)在后
      if (a.type === NodeType.CABINET && b.type === NodeType.ROOM) return -1;
      if (a.type === NodeType.ROOM && b.type === NodeType.CABINET) return 1;
      return 0; // 相同类型保持原顺序
    });

    return {
      title: (
        <TreeNode
          hasEditPermission={hasEditPermission}
          searchValue={searchValue}
          hasRemovePermission={hasRemovePermission}
          showIcon={showIcon}
          edit={edit}
          editVideo={editVideo}
          remove={remove}
          nodeData={item}
        />
      ),
      id: item.id,
      key: `type${item.type}-id${item.id}`,
      type: item.type,
      icon: (
        <img
          src={item.type === NodeType.ROOM ? circuitIcon : cabinetIcon}
          style={{ marginBottom: 4, width: 16, height: 16 }}
          alt=""
        />
      ),
      // 递归处理子节点，层级+1
      children: loop(children, showIcon, searchValue, edit, editVideo, remove, hasEditPermission, hasRemovePermission),
    };
  });
};

const DistributionCabinetManage: FC = () => {
  const routesContext = useBreadcrumbRoutes();
  const [openSider, setOpenSider] = useState(true);
  // 初始值
  const [siderWidth, setSiderWidth] = useState(320);
  // 拖拽状态
  const [dragStatus, setDragStatus] = useState(false);
  // 权限
  const hasAddPermission = useHasPermission(PermissionsType.B_CABINET_ADD);
  const hasEditPermission = useHasPermission(PermissionsType.B_CABINET_EDIT);
  const hasDeletePermission = useHasPermission(PermissionsType.B_CABINET_DELETE);

  const [addCabinetModal, setAddCabinetModal] = useState<boolean>(false);
  const [addRoomModal, setAddRoomModal] = useState<boolean>(false);
  const [addVideoModal, setAddVideoModal] = useState<boolean>(false);
  const [editNodeData, setEditNodeData] = useState<any>();
  const [update, setUpdate] = useUpdate();
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [selectedId, setSelectedId] = useState<Key>(); // 所选配电柜

  const [selectedKey, setSelectedKey] = useState<string>();

  const [searchValue, setSearchValue] = useState<string>('');
  const [treeData, setTreeData] = useState<RoomItem[]>([]);
  const [selectedType, setSelectedType] = useState<NodeType>();

  // 配电房
  useEffect(() => {
    apiV2DistributionCabinetRoomCabinetListPost({}).then(res => {
      setTreeData(res.list ?? []);

      if (!isNil(res.list?.[0].id) && !isNil(res.list?.[0].type)) {
        setSelectedId(res.list?.[0].id);
        setSelectedType(res.list?.[0].type);
        setSelectedKey(`type${res.list?.[0].type}-id${res.list?.[0].id}`);
      }
    });
  }, [update]);

  // 搜索
  const onSearch = (value: string) => {
    setSearchValue(value);
  };

  // 编辑button
  const switchStatus = () => {
    setShowEditIcon(!showEditIcon);
  };
  const editBtn = (node: any) => {
    setEditNodeData(node);
    if (node.type == 0) {
      setAddRoomModal(true);
    } else {
      setAddCabinetModal(true);
    }
  };
  const editVideo = (node: any) => {
    setEditNodeData(node);
    setAddVideoModal(true);
  };
  // 移除icon
  const removeBtn = (node: any) => {
    if (node.type === NodeType.ROOM && node.children.length > 0) {
      Modal.error({
        title: '该站房下配电柜绑定回路，无法删除，解绑后才能删除',
        okText: '确定',
      });
      return;
    }
    Modal.confirm({
      title: `确定删除该${node.type === NodeType.ROOM ? '站房' : '配电柜'}？删除后无法恢复`,
      okText: '确定',
      cancelText: '取消',
      onOk() {
        const delMethod =
          node.type == 0 ? apiV2DistributionCabinetDelDistributionRoomPost : apiV2DistributionCabinetDelPost;
        delMethod({ id: node.id }).then(setUpdate);
      },
    });
  };

  const formateTreeData = useMemo(() => {
    const filteredData = getFilteredTreeData(treeData, searchValue);
    return loop(
      filteredData,
      showEditIcon,
      searchValue,
      editBtn,
      editVideo,
      removeBtn,
      !!hasEditPermission,
      !!hasDeletePermission
    );
  }, [treeData, searchValue, showEditIcon, hasEditPermission, hasDeletePermission]);

  // 配电柜选择
  const onCabinetSelect = (selectedKeys: Key[], info: any) => {
    const key = info.node.key;
    const type = info.node.type;
    const id = info.node.id;

    setSelectedKey(key);
    setSelectedId(id);
    setSelectedType(type);
  };

  const dragChange = (width: number) => {
    if (openSider) {
      setSiderWidth(width);
    }
  };

  return (
    <Wrapper routes={routesContext?.routes ?? []} hasPadding={false}>
      <Layout className={styles.layout}>
        <Sider width={openSider ? siderWidth : 0} style={{ transition: dragStatus ? 'none' : 'all 0.2s' }}>
          <DragResize init={320} dragChange={dragChange} dragStatus={setDragStatus} />
          <div className={styles.content_left}>
            <div className={styles.searchArea}>
              <FormTitle title="站房/配电柜列表" />
              <Search placeholder="请输入站房/配电柜名称" allowClear onSearch={onSearch} />
              <Space size={8} style={{ marginTop: 20 }}>
                {hasAddPermission && (
                  <Dropdown
                    type="primary"
                    menu={{
                      items: [
                        {
                          key: '1',
                          label: '新建站房',
                          onClick: () => {
                            setEditNodeData(undefined);
                            setAddRoomModal(true);
                          },
                        },
                        {
                          key: '2',
                          label: '新建配电柜',
                          onClick: () => {
                            setEditNodeData(undefined);
                            setAddCabinetModal(true);
                          },
                        },
                      ],
                    }}
                    icon={<PlusOutlined />}
                  >
                    新建
                  </Dropdown>
                )}
                {hasEditPermission && formateTreeData.length > 0 && (
                  <Button type="primary" onClick={switchStatus}>
                    {showEditIcon ? '关闭' : '编辑'}
                  </Button>
                )}
              </Space>
            </div>

            <div className={styles.tree_outer}>
              <div className={styles.tree_area}>
                {formateTreeData.length === 0 ? (
                  <Empty style={{ paddingTop: '50px' }} />
                ) : (
                  <Tree
                    showIcon
                    defaultExpandAll
                    selectedKeys={!isNil(selectedKey) ? [selectedKey] : undefined}
                    treeData={formateTreeData}
                    onSelect={onCabinetSelect}
                  />
                )}
              </div>
            </div>
          </div>
        </Sider>
        <Content className={styles.content_right} style={{ marginLeft: openSider ? 10 : 0 }}>
          <div className="toggleBtn" style={{ left: openSider ? -30 : 0 }} onClick={() => setOpenSider(!openSider)}>
            {openSider ? <LeftOutlined /> : <RightOutlined />}
          </div>
          <FormTitle title="回路列表" />
          <CircuitList selectedId={selectedId} selectedType={selectedType} editVideo={editVideo} />
        </Content>
      </Layout>
      {addCabinetModal && (
        <AddCabinetModal
          updateFn={setUpdate}
          cabinet={editNodeData}
          visible={addCabinetModal}
          onClose={() => setAddCabinetModal(false)}
        />
      )}
      {addRoomModal && (
        <AddRoomModal
          visible={addRoomModal}
          room={editNodeData}
          updateFn={setUpdate}
          onClose={() => setAddRoomModal(false)}
        />
      )}
      {addVideoModal && (
        <AddVideoModal
          visible={addVideoModal}
          room={editNodeData}
          updateFn={setUpdate}
          onClose={() => setAddVideoModal(false)}
        />
      )}
    </Wrapper>
  );
};

export default DistributionCabinetManage;
